import { useState, useCallback, useEffect } from 'react';

export interface TutorialStep {
  step: number;
  targetElement?: string;
  message: string;
  buttonText?: string;
  waitForAction?: boolean;
  action?: () => void;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    step: 1,
    targetElement: 'catalog-button',
    message: 'Haz clic en "Catálogo" para ver todas las naves disponibles en Star Citizen.',
    waitForAction: true
  },
  {
    step: 2,
    targetElement: 'search-bar',
    message: 'Usa el buscador para encontrar naves específicas. Escribe "Aurora" para comenzar.',
    waitForAction: true
  },
  {
    step: 3,
    targetElement: 'add-ship-button',
    message: 'Haz clic en el botón "+1" para añadir tu primera nave a la flota.',
    waitForAction: true
  },
  {
    step: 4,
    targetElement: 'purchase-dinero-option',
    message: 'Selecciona "Dinero / Store Credits" para indicar cómo obtuviste la nave.',
    waitForAction: true
  },
  {
    step: 5,
    targetElement: 'insurance-lti-option',
    message: 'Selecciona "LTI" (Lifetime Insurance) para el seguro de la nave.',
    waitForAction: true
  },
  {
    step: 6,
    targetElement: 'insurance-confirm-button',
    message: 'Haz clic en "Confirmar" para finalizar la configuración del seguro.',
    waitForAction: true
  },
  {
    step: 7,
    targetElement: 'fleet-button',
    message: '¡Excelente! Ahora haz clic en "Flota" para ver tu nave añadida.',
    waitForAction: true
  },
  {
    step: 8,
    targetElement: 'individual-view-button',
    message: 'Haz clic en "Individuales" para ver las naves una por una en lugar de agrupadas.',
    waitForAction: true
  },
  {
    step: 9,
    targetElement: 'delete-ship-button-aurora-cl',
    message: 'Haz clic en el botón de papelera para eliminar una nave de tu flota.',
    waitForAction: true
  },
  {
    step: 10,
    targetElement: 'confirm-delete-button',
    message: 'Confirma el borrado haciendo clic en el botón "Confirmar" del modal que aparece.',
    waitForAction: true,
    buttonText: 'Completar tutorial'
  }
];

const TUTORIAL_STORAGE_KEY = 'fleetmanager-tutorial-completed';

export const useTutorial = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  // Verificar si el tutorial ya fue completado
  useEffect(() => {
    const completed = localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true';
    setIsCompleted(completed);
  }, []);

  const startTutorial = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    const nextStepIndex = currentStep + 1;
    
    if (nextStepIndex >= TUTORIAL_STEPS.length) {
      // Tutorial completado
      setIsActive(false);
      setIsCompleted(true);
      localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    } else {
      setCurrentStep(nextStepIndex);
    }
  }, [currentStep]);

  const skipTutorial = useCallback(() => {
    setIsActive(false);
    setIsCompleted(true);
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
  }, []);

  const resetTutorial = useCallback(() => {
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
    setIsCompleted(false);
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const getCurrentStep = useCallback((): TutorialStep | null => {
    return TUTORIAL_STEPS[currentStep] || null;
  }, [currentStep]);

  const triggerStepAction = useCallback((targetElement: string) => {
    const current = getCurrentStep();
    if (current && current.targetElement === targetElement && current.waitForAction) {
      nextStep();
    }
  }, [getCurrentStep, nextStep]);

  // Función para verificar si nunca se ha completado el tutorial
  const hasNeverCompletedTutorial = useCallback(() => {
    return localStorage.getItem(TUTORIAL_STORAGE_KEY) !== 'true';
  }, []);

  return {
    isActive,
    currentStep: currentStep + 1, // 1-indexed for UI
    totalSteps: TUTORIAL_STEPS.length,
    isCompleted,
    hasNeverCompletedTutorial,
    startTutorial,
    nextStep,
    skipTutorial,
    resetTutorial,
    getCurrentStep,
    triggerStepAction
  };
};
